import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import WineCard from "@/components/WineCard";
import WineDetailDialog from "@/components/WineDetailDialog";
import FiltersBar, { EMPTY_FILTERS } from "@/components/FiltersBar";
import { Input } from "@/components/ui/input";
import { Search, Info, LayoutGrid, BookOpen, ArrowUp, X, Home as HomeIcon } from "lucide-react";
import PaperListView from "@/components/PaperListView";
import BackButton from "@/components/BackButton";
import { useNavigate } from "react-router-dom";
import { useStaffStockVisibility, StaffStockToggle } from "@/hooks/useStaffStockVisibility";

const STORAGE_KEY = "co_catalog_state";
const PAGE_SIZE = 30;

function loadStoredState() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
}

export default function Catalog() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [urlParams] = useSearchParams();
    const initial = useMemo(() => {
        const stored = loadStoredState() || { search: "", filters: { ...EMPTY_FILTERS } };
        // URL params override stored (deep-link support from Home + Map drill-down)
        const urlQ = urlParams.get("q");
        const urlTipo = urlParams.get("tipo");
        const urlRegione = urlParams.get("regione");
        const urlPaese = urlParams.get("paese");
        const search = urlQ !== null ? urlQ : stored.search;
        // Any URL filter param → reset to EMPTY and apply URL filters (so map/Home drill-down
        // is deterministic and doesn't inherit stale localStorage filters).
        let filters;
        if (urlTipo || urlRegione || urlPaese) {
            filters = { ...EMPTY_FILTERS };
            if (urlTipo) filters.tipo = urlTipo;
            if (urlRegione) filters.regione = urlRegione;
            if (urlPaese) filters.paese = urlPaese;
        } else {
            filters = stored.filters || { ...EMPTY_FILTERS };
        }
        return { search, filters };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [search, setSearch] = useState(initial.search);
    const [filters, setFilters] = useState(initial.filters);
    // Fase 7.6: quali filtri sono attivi (per uscite fine lista + sticky bar)
    const hasActiveFilters = useMemo(() =>
        Object.entries(filters).some(([, v]) =>
            v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)
        ),
        [filters]);
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [firstLoad, setFirstLoad] = useState(true);
    const [error, setError] = useState(null);
    const [openWine, setOpenWine] = useState(null);
    const [facets, setFacets] = useState(null);
    const [viewMode, setViewMode] = useState(() => {
        try {
            return localStorage.getItem("co_catalog_view") || "grid";
        } catch { return "grid"; }
    });
    const sentinelRef = useRef(null);
    const skipRef = useRef(0);

    // Deep-link: /carta?wine=<id> apre subito la scheda vino (usato dalla chat
    // "Consigliami" e dalla mappa quando si arriva da un altra pagina).
    useEffect(() => {
        const w = urlParams.get("wine");
        if (w) setOpenWine(w);
    }, [urlParams]);

    // Persist view mode
    useEffect(() => {
        try { localStorage.setItem("co_catalog_view", viewMode); } catch {}
    }, [viewMode]);

    const isAnon = !user;
    const isUnverified = user && !user.email_verified;
    const canSeePrices = true;  // Fase 6.8: listino pubblico visibile a tutti.
    const isTradeVipUnverified = isUnverified && (user?.role === "trade" || user?.role === "vip");
    const isStaff = user && (user.role === "staff" || user.role === "admin");
    // Toggle "mostra/nascondi giacenze" — solo staff/admin, pura presentazione.
    const { showStock, toggle: toggleShowStock } = useStaffStockVisibility();

    useEffect(() => {
        const h = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(h);
    }, [search]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ search, filters }));
    }, [search, filters]);

    const buildParams = useCallback(() => {
        const p = { limit: PAGE_SIZE };
        if (debouncedSearch) p.q = debouncedSearch;
        if (filters.tipo) p.tipo = filters.tipo;
        if (filters.paese) p.paese = filters.paese;
        if (filters.regione) p.regione = filters.regione;
        if (filters.produttore) p.produttore = filters.produttore;
        if (filters.annata) p.annata = filters.annata;
        if (filters.formato) p.formato = filters.formato;
        if (filters.calice) p.calice = true;
        if (canSeePrices && filters.prezzo_min !== "") p.prezzo_min = filters.prezzo_min;
        if (canSeePrices && filters.prezzo_max !== "") p.prezzo_max = filters.prezzo_max;
        if (Array.isArray(filters.vitigni) && filters.vitigni.length > 0) {
            p.vitigni = filters.vitigni.join(",");
        }
        return p;
    }, [debouncedSearch, filters, canSeePrices]);

    useEffect(() => {
        const params = buildParams();
        setLoading(true);
        setError(null);
        skipRef.current = 0;
        // Fetch pagina + facets in parallelo, così i conteggi sui filtri
        // sono sempre allineati al set risultato corrente.
        Promise.all([
            api.get("/wines", { params: { ...params, skip: 0 } }),
            api.get("/wines/facets", { params: { ...params, skip: undefined, limit: undefined } }),
        ])
            .then(([{ data }, { data: fdata }]) => {
                setItems(data.items);
                setTotal(data.total);
                setHasMore(data.has_more);
                skipRef.current = data.items.length;
                setFacets(fdata);
            })
            .catch(() => setError("Errore nel caricamento della carta."))
            .finally(() => {
                setLoading(false);
                setFirstLoad(false);
            });
    }, [buildParams]);

    useEffect(() => {
        if (!sentinelRef.current) return;
        const el = sentinelRef.current;
        const io = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (!entry.isIntersecting || loading || !hasMore) return;
                setLoading(true);
                api
                    .get("/wines", { params: { ...buildParams(), skip: skipRef.current } })
                    .then(({ data }) => {
                        setItems((prev) => [...prev, ...data.items]);
                        setHasMore(data.has_more);
                        skipRef.current += data.items.length;
                    })
                    .finally(() => setLoading(false));
            },
            { rootMargin: "300px" }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [hasMore, loading, buildParams]);

    let visibilityNotice = null;
    // Fase 6.8: listino pubblico visibile a tutti; nessun notice invasivo.
    // Manteniamo un promemoria discreto per Trade/VIP non verificati
    // (mostrato solo nel dettaglio, non nella lista).

    return (
        <main>
            <section className="co-container pt-6 sm:pt-8 pb-4">
                <BackButton testid="catalog-back-btn" />
            </section>
            <section className="co-container pt-2 sm:pt-4 pb-6">
                <div className="max-w-3xl">
                    <span className="co-caption block mb-3">Casa Orterie · Stazzona</span>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[hsl(var(--foreground))]">
                        Tutta la <em className="italic text-[hsl(var(--primary))]">Carta dei Vini</em>
                    </h1>
                    <p className="mt-5 text-[hsl(var(--muted-foreground))] max-w-xl leading-relaxed">
                        Una selezione curata di etichette italiane e internazionali. Sfoglia, cerca, filtra.
                    </p>
                </div>
            </section>

            <section className="co-container sticky top-20 z-20 bg-[hsl(var(--background))]/95 backdrop-blur-md py-4 co-hairline -mx-5 sm:-mx-8 lg:-mx-10 px-5 sm:px-8 lg:px-10">
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />
                            <Input
                                data-testid="catalog-search-input"
                                placeholder="Cerca per nome, produttore, note…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 bg-white h-11"
                            />
                        </div>
                        {/* Fase 7.1: toggle Griglia ↔ Vista carta reso prominente
                            con label testuale, posizionato subito accanto alla
                            ricerca (non nascosto tra i filtri). */}
                        <div
                            className="inline-flex border border-[hsl(var(--border))] rounded-sm overflow-hidden bg-white shrink-0 self-start sm:self-auto"
                            data-testid="catalog-view-toggle"
                            role="tablist"
                            aria-label="Modalità di visualizzazione"
                        >
                            <button
                                type="button"
                                onClick={() => setViewMode("grid")}
                                className={`flex items-center gap-1.5 px-3 sm:px-4 h-11 text-sm transition-colors ${
                                    viewMode === "grid"
                                        ? "bg-[hsl(var(--primary))] text-white"
                                        : "hover:bg-black/[0.03] text-[hsl(var(--foreground))]"
                                }`}
                                data-testid="catalog-view-grid-btn"
                                aria-pressed={viewMode === "grid"}
                                title="Vista a griglia con foto"
                            >
                                <LayoutGrid className="w-4 h-4" strokeWidth={1.5} />
                                <span>Griglia</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode("paper")}
                                className={`flex items-center gap-1.5 px-3 sm:px-4 h-11 text-sm transition-colors ${
                                    viewMode === "paper"
                                        ? "bg-[hsl(var(--primary))] text-white"
                                        : "hover:bg-black/[0.03] text-[hsl(var(--foreground))]"
                                }`}
                                data-testid="catalog-view-paper-btn"
                                aria-pressed={viewMode === "paper"}
                                title="Vista carta tradizionale"
                            >
                                <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                                <span>Vista carta</span>
                            </button>
                        </div>
                    </div>
                    <FiltersBar
                        value={filters}
                        onChange={setFilters}
                        showPriceFilter={true}
                        facets={facets}
                        total={total}
                    />
                    {/* Fase 7.6: pill "Azzera filtri" nella sticky bar,
                        sempre visibile anche dopo scroll quando ci sono filtri attivi. */}
                    {(hasActiveFilters || search) && (
                        <div className="flex justify-end">
                            <button
                                onClick={() => {
                                    setSearch("");
                                    setFilters({ ...EMPTY_FILTERS });
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] rounded-full text-xs hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))] transition-colors"
                                data-testid="catalog-clear-filters-sticky-btn"
                            >
                                <X className="w-3 h-3" strokeWidth={1.5} /> Azzera filtri
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Fase 6.8: banner "Accedi per prezzi" rimosso — listino pubblico visibile a tutti */}

            <section className="co-container py-8">
                <div className="mb-6 flex items-baseline justify-between gap-3">
                    <p className="co-caption" data-testid="catalog-total">
                        {total === 0 && !firstLoad ? "Nessun risultato" : `${total} etichett${total === 1 ? "a" : "e"}`}
                    </p>
                    {isStaff && (
                        <div className="flex items-center gap-3">
                            <p className="co-caption text-[hsl(var(--primary))]">
                                Vista Staff · {showStock ? "Giacenze visibili" : "Giacenze nascoste"}
                            </p>
                            <StaffStockToggle showStock={showStock} onToggle={toggleShowStock} />
                        </div>
                    )}
                </div>

                {error && <div className="text-sm text-[hsl(var(--destructive))]">{error}</div>}

                {!loading && !firstLoad && items.length === 0 && !error && (
                    <div className="py-16 text-center max-w-md mx-auto" data-testid="catalog-empty-state">
                        <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-[hsl(var(--secondary))]">
                            <Info className="w-5 h-5 text-[hsl(var(--muted-foreground))]" strokeWidth={1.2} />
                        </div>
                        <p className="font-serif text-2xl mb-2">Nessuna etichetta trovata</p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">
                            Prova ad allargare la ricerca — cambia tipologia, zona o azzera i filtri.
                            La nostra carta cambia spesso: torna presto.
                        </p>
                    </div>
                )}

                <div data-testid="catalog-items-wrap">
                    {viewMode === "paper" ? (
                        <PaperListView
                            wines={items}
                            canSeePrices={canSeePrices}
                            noticeForNoPrice={visibilityNotice}
                            onWineClick={(id) => setOpenWine(id)}
                        />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                            {items.map((w) => (
                                <WineCard
                                    key={w.id}
                                    wine={w}
                                    onOpen={() => setOpenWine(w.id)}
                                    showStock={showStock}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {loading && !firstLoad && (
                    <div className="mt-8 text-center co-caption">Caricamento…</div>
                )}

                <div ref={sentinelRef} className="h-10" />

                {/* Fase 7.6: uscite di fine lista — evita il vicolo cieco
                    quando una ricerca stretta ritorna pochi/zero risultati. */}
                {!hasMore && (
                    <div className="mt-8 pt-6 border-t border-[hsl(var(--border))] flex flex-col items-center gap-4 pb-4">
                        {items.length > 0 && (
                            <p className="co-caption" data-testid="catalog-end-of-list">Fine della carta</p>
                        )}
                        <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                            {(hasActiveFilters || search) && (
                                <button
                                    onClick={() => {
                                        setSearch("");
                                        setFilters({ ...EMPTY_FILTERS });
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] rounded-full text-sm hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))] transition-colors"
                                    data-testid="catalog-clear-filters-end-btn"
                                >
                                    <X className="w-3.5 h-3.5" strokeWidth={1.5} /> Azzera filtri
                                </button>
                            )}
                            <button
                                onClick={() => navigate("/")}
                                className="inline-flex items-center gap-1.5 px-4 py-2 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
                                data-testid="catalog-home-end-btn"
                            >
                                <HomeIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Torna alla home
                            </button>
                            <button
                                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                                className="inline-flex items-center gap-1.5 px-4 py-2 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
                                data-testid="catalog-scroll-top-btn"
                            >
                                <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} /> Torna su
                            </button>
                        </div>
                    </div>
                )}
            </section>

            <WineDetailDialog
                wineId={openWine}
                open={Boolean(openWine)}
                onOpenChange={(v) => !v && setOpenWine(null)}
                canSeePrices={canSeePrices}
                isTradeVipUnverified={isTradeVipUnverified}
            />
        </main>
    );
}
